import { getApiBaseUrl } from '../config/apiConfig';

// Service d'authentification pour l'API Symfony
const API_CONFIG = {
  baseUrl: getApiBaseUrl(),
  timeout: 30000
};

class AuthService {
  constructor() {
    this.token = localStorage.getItem('auth_token');
    this.user = JSON.parse(localStorage.getItem('auth_user') || 'null');
  }

  // Vérifier si l'utilisateur est connecté
  isAuthenticated() {
    return !!this.token && !!this.user;
  }

  // Vérifier si l'utilisateur a un rôle spécifique
  hasRole(role) {
    if (!this.user || !this.user.roles) return false;
    // Gérer les rôles Symfony avec ou sans le préfixe ROLE_
    const normalizedRole = role.startsWith('ROLE_') ? role : `ROLE_${role}`;
    return this.user.roles.includes(normalizedRole) || this.user.roles.includes(role);
  }

  // Vérifier si l'utilisateur est admin ou super admin
  isAdmin() {
    return this.hasRole('ROLE_ADMIN') || this.hasRole('ROLE_SUPERADMIN');
  }

  // Vérifier si l'utilisateur est super admin
  isSuperAdmin() {
    return this.hasRole('ROLE_SUPERADMIN');
  }

  // Vérifier si l'utilisateur est un utilisateur simple
  isUser() {
    return this.hasRole('ROLE_USER');
  }

  isTmProjectManager() {
    return this.hasRole('ROLE_TM_PROJECT_MANAGER') || this.isAdmin();
  }

  isCommercial() {
    return this.hasRole('ROLE_COMMERCIAL') || this.hasRole('ROLE_ADMIN_COMMERCIAL');
  }

  isAdminCommercial() {
    return this.hasRole('ROLE_ADMIN_COMMERCIAL');
  }

  isGraphiste() {
    return this.hasRole('ROLE_GRAPHISTE');
  }

  isProd() {
    return this.hasRole('ROLE_PROD');
  }

  canSeeTeamDepots() {
    return this.isSuperAdmin() || this.isAdminCommercial();
  }

  // Obtenir l'utilisateur actuel
  getCurrentUser() {
    return this.user;
  }

  // Obtenir le token d'authentification
  getToken() {
    return this.token;
  }

  // Connexion
  async login(email, password) {
    try {
      const response = await fetch(`${API_CONFIG.baseUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password: password
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur de connexion');
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        // Sauvegarder le token et les informations utilisateur
        this.token = data.data.token;
        this.user = data.data.user;
        
        localStorage.setItem('auth_token', this.token);
        localStorage.setItem('auth_user', JSON.stringify(this.user));
        this.startSessionKeepAlive();
        
        return {
          success: true,
          user: this.user,
          token: this.token
        };
      } else {
        throw new Error(data.message || 'Erreur de connexion');
      }
    } catch (error) {
      console.error('Erreur lors de la connexion:', error);
      throw error;
    }
  }

  // Déconnexion
  logout() {
    this.stopSessionKeepAlive();
    this.token = null;
    this.user = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    import('./menuVisibilityService')
      .then((mod) => mod.default.clear())
      .catch(() => {});
  }

  // Vérifier la validité du token
  async validateToken() {
    if (!this.token) return false;

    try {
      const response = await fetch(`${API_CONFIG.baseUrl}/auth/validate`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          // Mettre à jour les informations utilisateur
          this.user = data.data.user;
          localStorage.setItem('auth_user', JSON.stringify(this.user));
          return true;
        }
      }
      
      // Token invalide, déconnecter
      this.logout();
      return false;
    } catch (error) {
      console.error('Erreur lors de la validation du token:', error);
      this.logout();
      return false;
    }
  }

  // Obtenir les headers d'authentification pour les requêtes API
  getAuthHeaders() {
    return {
      'Authorization': `Bearer ${this.token}`,
      'Content-Type': 'application/json'
    };
  }

  // Rafraîchir le token (si nécessaire)
  async refreshToken() {
    if (!this.token) return false;

    try {
      const response = await fetch(`${API_CONFIG.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          this.token = data.data.token;
          localStorage.setItem('auth_token', this.token);
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Erreur lors du rafraîchissement du token:', error);
      return false;
    }
  }

  /** Prolonge la session tant que l'utilisateur utilise l'application. */
  startSessionKeepAlive(intervalMs = 45 * 60 * 1000) {
    this.stopSessionKeepAlive();
    this._keepAliveTimer = setInterval(() => {
      if (document.visibilityState !== 'visible' || !this.token) {
        return;
      }
      this.refreshToken().catch(() => {});
    }, intervalMs);
  }

  stopSessionKeepAlive() {
    if (this._keepAliveTimer) {
      clearInterval(this._keepAliveTimer);
      this._keepAliveTimer = null;
    }
  }

}

// Créer une instance singleton
const authService = new AuthService();

export default authService;
